import { CircularProgress, createStyles, makeStyles, Theme } from '@material-ui/core';
import React, { Fragment, useEffect, useState } from 'react';

import { GetLoggedInUserQuery, OnboardingStep, useGetLoggedInUserQuery } from '../../../graphql/gen/types';
import { logAnalytics, useLogAnalytics } from '../shared/logAnalytics';
import { GetPluggedIntoPlex } from './GetPluggedIntoPlex';
import { GetStarted } from './GetStarted';
import { KnowEachOther } from './know_each_other/KnowEachOther';
import { NavBar } from './NavBar';

interface Props {
  userProfile: NonNullable<GetLoggedInUserQuery['currentUser']>;
  onClose: () => void;
}

const onboardingStepsOrder = [
  OnboardingStep.GetStarted,
  OnboardingStep.GetPluggedIntoPlex,
  OnboardingStep.KnowEachOther,
];

export const Onboarding = ({ userProfile, onClose }: Props): React.ReactElement => {
  useLogAnalytics({ name: 'started onboarding' });
  const classes = useStyles();
  const { data: currentUser, loading } = useGetLoggedInUserQuery({ variables: {} });
  const [currentOnboardingPage, setCurrentOnboardingPage] = useState<OnboardingStep | null>(null);

  const onComplete = (): void => {
    logAnalytics({ name: 'completed onboarding' });
    onClose();
  };

  useEffect(() => {
    if (!currentUser) {
      return;
    }
    // exit if we already set the initial onboarding page
    if (!!currentOnboardingPage) {
      return;
    }
    // set the initial onboarding step as the user's first uncompleted onboarding step
    const completedOnboardingSteps = currentUser?.currentUser?.completedOnboardingSteps || [];
    if (completedOnboardingSteps.length > 0) {
      for (const step of onboardingStepsOrder) {
        if (completedOnboardingSteps.indexOf(step) === -1) {
          setCurrentOnboardingPage(step);
          return;
        }
      }
    }
    // if all steps (or none) are completed then send user to the first page of the onboarding
    setCurrentOnboardingPage(OnboardingStep.GetStarted);
  }, [currentOnboardingPage, currentUser, setCurrentOnboardingPage]);

  if (!currentOnboardingPage || loading) {
    return <CircularProgress size={24} />;
  }

  return (
    <Fragment>
      <NavBar onClose={onClose} />
      <div className={classes.contentWrapper}>
        <div className={classes.content}>
          {currentOnboardingPage === OnboardingStep.GetStarted && (
            <GetStarted
              userProfile={userProfile}
              onNext={(): void => {
                setCurrentOnboardingPage(OnboardingStep.GetPluggedIntoPlex);
              }}
            />
          )}
          {currentOnboardingPage === OnboardingStep.GetPluggedIntoPlex && (
            <GetPluggedIntoPlex
              changeOnboardingStep={setCurrentOnboardingPage}
              completedOnboardingSteps={currentUser?.currentUser?.completedOnboardingSteps || []}
              currentStep={currentOnboardingPage}
              onNext={(): void => {
                setCurrentOnboardingPage(OnboardingStep.KnowEachOther);
              }}
            />
          )}
          {currentOnboardingPage === OnboardingStep.KnowEachOther && (
            <KnowEachOther
              changeOnboardingStep={setCurrentOnboardingPage}
              completedOnboardingSteps={currentUser?.currentUser?.completedOnboardingSteps || []}
              currentStep={currentOnboardingPage}
              onNext={onComplete}
            />
          )}
        </div>
      </div>
    </Fragment>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    contentWrapper: {
      backgroundColor: '#F6F6F6',
    },
    content: {
      display: 'flex',
      flexDirection: 'row',
    },
  })
);
